import React, { useEffect, useRef, useState } from "react";

// The session typesets the whole page synchronously on every activity update.
// MathJax skips this class on that pass, so the formula is only ever typeset
// by its own call below.
const SKIPPED_BY_PAGE_WIDE_TYPESET = "mathjax_ignore";

// min(): the box shrinks to the card on a narrow screen and scrolls sideways
// there. overflowY: MathJax's line-break spacers overhang the box, and
// "auto" would give every fraction a scrollbar. The font is .prompt's, because
// MathJax sizes math against the text around it.
const FORMULA_BOX = {
  maxWidth: "min(680px, 100%)",
  overflowX: "auto",
  overflowY: "hidden",
  padding: "4px 0",
  textAlign: "center",
  fontFamily: '"MathJax Main Regular"',
  fontSize: "20px",
};
const HIDDEN_UNTIL_TYPESET = { visibility: "hidden", height: 0, padding: 0, overflowX: "hidden" };

// MathJax draws a command it does not know as that command's name in red, with
// no error node.
const spellsOutUnknownCommand = (formula) =>
  Array.from(formula.querySelectorAll("mjx-mtext")).some((text) => text.textContent.startsWith("\\"));

const isRenderedWithoutError = (formula) =>
  formula.querySelector("mjx-container") !== null &&
  formula.querySelector("mjx-merror") === null &&
  !spellsOutUnknownCommand(formula);

export const hasFormulaText = (latex) => typeof latex === "string" && latex.trim() !== "";

export const PromptFormula = ({ latex, fallback }) => {
  const formulaRef = useRef(null);
  const [typesetLatex, setTypesetLatex] = useState(null);
  const isTypeset = typesetLatex === latex;

  useEffect(() => {
    const formula = formulaRef.current;
    const discardFormula = () => {
      formula.textContent = "";
    };
    const tex = `\\displaystyle ${latex}`;
    let isCurrent = true;

    const typesetFormula = async () => {
      // Loads whatever the formula needs (\enclose is fetched on demand) before
      // its element is handed over. A page-wide pass that runs while a handed
      // over element still waits for an extension typesets it wrong, with no
      // error; after this there is nothing left to wait for.
      await window.MathJax.tex2chtmlPromise(tex);
      if (!isCurrent) return;
      formula.textContent = `\\(${tex}\\)`;
      await window.MathJax.typesetPromise([formula]);
      if (!isCurrent) return;
      if (isRenderedWithoutError(formula)) setTypesetLatex(latex);
      else discardFormula();
    };

    typesetFormula().catch(discardFormula);
    return () => {
      isCurrent = false;
      discardFormula();
    };
  }, [latex]);

  return (
    <>
      {!isTypeset && fallback}
      <div
        className={SKIPPED_BY_PAGE_WIDE_TYPESET}
        style={isTypeset ? FORMULA_BOX : { ...FORMULA_BOX, ...HIDDEN_UNTIL_TYPESET }}
      >
        <span ref={formulaRef} />
      </div>
    </>
  );
};
